import { badgeColors } from '@/lib/badgeContrast';
import React, { useState, useEffect, useMemo } from 'react';
import { Conversation, Lead } from '@/types';
import { Search, User, CheckCheck, Menu, Clock, AlertCircle, Filter, Building2, Archive, ArchiveRestore, BellOff, X, ArrowLeft, Mic, Image, FileText, Video as VideoIcon } from 'lucide-react';
import { cn, formatSafe, cleanMetaText, safeLower, safeText, getCleanedFriendlyPreview } from '@/lib/utils';
import { LeadAvatar } from '../LeadAvatar';
import { useAuthStore } from '@/store/authStore';
import { useAdminStore } from '@/store/adminStore';
import { useWhatsAppStore } from '@/store/whatsappStore';
import { useConversationsStore } from '@/store/conversationsStore';
import { useConversations } from '@/lib/query/useConversations';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { useTagsStore } from '@/store/tagsStore';
import { useUsersStore } from '@/store/usersStore';
import { normalizeLeadForUI, notifySound, useLeadsStore } from '@/store/leadsStore';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { ChatListItem } from '@/components/ui/whatsapp/chat-list-item';

interface Props {
  leads: Lead[];
  selectedLeadId: string | null;
  onSelectLead: (id: string, lead?: Lead, conversation?: Conversation) => void;
  onSelectMultipleLeads?: (ids: string[]) => void;
  showArchived?: boolean;
  onToggleArchived?: (val: boolean) => void;
  selectedTenantId?: string;
  onTenantChange?: (tenantId: string) => void;
}

export function LeadListSidebar({
  leads,
  selectedLeadId,
  onSelectLead,
  onSelectMultipleLeads,
  showArchived = false,
  onToggleArchived,
  selectedTenantId = '',
  onTenantChange
}: Props) {
  // Referência única da instância do Sidebar para controle de alerts (B3)
  const sidebarOwnerRef = React.useRef(Symbol('sidebar-alert-scope'));

  const [filter, setFilter] = useState('todos');
  const [mainTab, setMainTab] = useState<'minhas' | 'todas' | 'transbordo'>('minhas');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    if (!search.trim()) { setDebouncedSearch(''); return; }
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const [displayCount, setDisplayCount] = useState(150);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [notificationPermission, setNotificationPermission] = useState(() =>
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported');
  const [dismissedBannerScope, setDismissedBannerScope] = useState<string | null>(null);

  // Solicitar permissão nativa ao clicar no botão do banner
  const handleEnableDesktopNotifications = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        toast.error('Notificações de área de trabalho não são suportadas neste navegador.');
        return;
      }
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('Notificações de área de trabalho ativadas com sucesso!');
        notifySound();
      } else if (permission === 'denied') {
        toast.error('Notificações bloqueadas nas configurações do seu navegador.');
      }
    } catch (err) {
      console.error('[NOTIFICATIONS] Erro ao solicitar permissão de notificações:', err);
    }
  };

  // Active Loja filter
  const { lojaAtivaId } = useLojaAtivaStore();

  const isSuperAdmin = useAuthStore(state => state.isSuperAdmin);
  const profile = useAuthStore(state => state.profile);
  const user = useAuthStore(state => state.user);
  const currentUserId = profile?.id || user?.id;
  // Only an opaque auth ID scopes the cache; no contact content or permission is stored.
  const notificationBannerKey = user?.id ? `task-crm:desktop-banner:v1:${user.id}` : null;
  useEffect(() => {
    try { setDismissedBannerScope(notificationBannerKey && localStorage.getItem(notificationBannerKey) === 'dismissed' ? notificationBannerKey : null); }
    catch { setDismissedBannerScope(null); }
    const refresh = () => setNotificationPermission('Notification' in window ? Notification.permission : 'unsupported');
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [notificationBannerKey]);
  const dismissNotificationBanner = () => {
    setDismissedBannerScope(notificationBannerKey);
    if (notificationBannerKey) {
      try { localStorage.setItem(notificationBannerKey, 'dismissed'); } catch { /* Browser cache is optional. */ }
    }
  };
  const tenants = useAdminStore(state => state.tenants);
  const fetchTenants = useAdminStore(state => state.fetchTenants);
  const connections = useWhatsAppStore(state => state.connections);
  const fetchConnections = useWhatsAppStore(state => state.fetchConnections);
  const users = useUsersStore(state => state.users);
  const fetchUsers = useUsersStore(state => state.fetchUsers);
  // Subscribe to catalog changes as well as the stable resolver function.
  const { resolveTag, fetchTagsForLoja } = useTagsStore();
  const archiveConversation = useConversationsStore(state => state.archiveConversation);

  const {
    conversations,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    refetch,
    isError,
    syncError,
    isLoading
  } = useConversations({
    userId: currentUserId,
    lojaId: lojaAtivaId || undefined,
    tenantId: selectedTenantId || undefined,
    archived: showArchived,
    transbordo: !showArchived && mainTab === 'transbordo',
    myOnly: !showArchived && mainTab === 'minhas',
    q: debouncedSearch || undefined
  });

  const [archivedCount, setArchivedCount] = useState<{ scope: string; count: number } | null>(null);
  const archiveScope = JSON.stringify([currentUserId, selectedTenantId, lojaAtivaId]);
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const count = await api.getConversationsCount({ tenant_id: selectedTenantId || undefined, loja_id: lojaAtivaId || undefined, archived: true });
        if (active) setArchivedCount({ scope: archiveScope, count });
      } catch { if (active) setArchivedCount(null); }
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [archiveScope, conversations]);

  const [transbordoCount, setTransbordoCount] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;
    const updateCount = async () => {
      try {
        const count = await api.getConversationsCount({
          tenant_id: selectedTenantId || undefined,
          loja_id: lojaAtivaId || undefined,
          transbordo: true,
          archived: false
        });
        if (isMounted) setTransbordoCount(count);
      } catch (e) {
        // Ignore
      }
    };
    updateCount();
    const interval = setInterval(updateCount, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [selectedTenantId, lojaAtivaId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reatividade em tempo real quando o responsável do lead muda
  useEffect(() => {
    // O leadsStore já é reativo — quando o lead muda, o componente re-renderiza
    // O badge vai mostrar o novo nome automaticamente
  }, [leads]);

  // P-ASSIGN-ID: Set unificado com todos os identificadores conhecidos do usuário logado (CRM ID, Auth ID)
  const myUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (profile?.id) ids.add(profile.id);
    if (profile?.auth_user_id) ids.add(profile.auth_user_id);
    if ((profile as any)?.crm_user_id) ids.add((profile as any).crm_user_id);
    if (user?.id) ids.add(user.id);
    if (currentUserId) ids.add(currentUserId);

    // Mapear usuário correspondente no usersStore para extrair id e auth_user_id
    const matchedUser = users.find(u =>
      (profile?.id && (u.id === profile.id || u.auth_user_id === profile.id)) ||
      (profile?.auth_user_id && (u.id === profile.auth_user_id || u.auth_user_id === profile.auth_user_id)) ||
      (user?.id && (u.id === user.id || u.auth_user_id === user.id)) ||
      (profile?.email && u.email?.toLowerCase() === profile.email.toLowerCase())
    );
    if (matchedUser) {
      if (matchedUser.id) ids.add(matchedUser.id);
      if (matchedUser.auth_user_id) ids.add(matchedUser.auth_user_id);
    }
    return ids;
  }, [profile, user, currentUserId, users]);

  const isAssignedToMe = (targetConv?: any) => {
    if (!targetConv) return false;
    return Boolean(
      (targetConv.assigned_user_id && myUserIds.has(targetConv.assigned_user_id)) ||
      (targetConv.assigned_auth_user_id && myUserIds.has(targetConv.assigned_auth_user_id)) ||
      (targetConv.assigned_crm_user_id && myUserIds.has(targetConv.assigned_crm_user_id))
    );
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants(true);
    }
    fetchConnections();
  }, [isSuperAdmin, fetchTenants, fetchConnections]);

  useEffect(() => {
    localStorage.removeItem('inbox_filter_connection');
  }, []);

  // Sincroniza selectedTenantId em contexto transitório de ALERTAS do store de forma isolada por instância (B3)
  useEffect(() => {
    const owner = sidebarOwnerRef.current;
    useLeadsStore.getState().setAlertScope?.(owner, selectedTenantId || null);
    return () => {
      // Logout/unmount limpam somente seu próprio contexto (B3)
      useLeadsStore.getState().releaseAlertScope?.(owner);
    };
  }, [selectedTenantId]);

  const handleTenantChange = (tenantId: string) => {
    // antes de encaminhar troca ao pai (B3)
    const owner = sidebarOwnerRef.current;
    useLeadsStore.getState().setAlertScope?.(owner, tenantId || null);
    onTenantChange?.(tenantId);
    if (tenantId) {
      localStorage.setItem('inbox_filter_tenant', tenantId);
    } else {
      localStorage.removeItem('inbox_filter_tenant');
    }
  };

  useEffect(() => {
    setDisplayCount(150);
  }, [search, filter, mainTab, showArchived]);

  const cleanPreviewText = (preview?: string) => {
    if (!preview) return 'Sem mensagens ainda';
    const cleaned = getCleanedFriendlyPreview(preview);
    if (!cleaned || cleaned === 'Envie uma mensagem') return 'Clique para conversar';
    if (cleaned.startsWith("🤖 ")) return cleaned.substring(3);
    if (cleaned.startsWith("👤 ")) return cleaned.substring(3);
    return cleaned;
  };

  // J1: Hook para forçar re-render a cada 60s e atualizar tags "Aguardando Xh"
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Helper to compute waiting hours if sender_type = lead
  const getWaitingHoursText = (lastMsgAt?: string, senderType?: string) => {
    if (!lastMsgAt || senderType !== 'lead') return null;
    const diffMs = now - new Date(lastMsgAt).getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours >= 1) {
      if (hours >= 24) {
        const days = Math.floor(hours / 24);
        return `Aguardando ${days}d`;
      }
      return `Aguardando ${hours}h`;
    }
    return null;
  };

  const toggleSelectLead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
      if (onSelectMultipleLeads) onSelectMultipleLeads(next);
      return next;
    });
  };

  const filterOptions = [
    { id: 'todos', label: 'Todos' },
    { id: 'nao_lidos', label: 'Não lidos' },
    { id: 'em_atendimento', label: 'Em atendimento' },
    { id: 'agendados', label: 'Agendados' },
    { id: 'finalizados', label: 'Finalizados' },
  ];

  const renderLastMessagePreview = (lead: Lead, hasUnread: boolean) => {
    // Typing indicator da IA
    const conv = (lead as any).conversation;
    const isAiTyping = Boolean(
      ((lead as any).ai_typing || conv?.ai_typing) &&
      ((lead as any).ai_typing_at || conv?.ai_typing_at) &&
      new Date((lead as any).ai_typing_at || conv?.ai_typing_at).getTime() > Date.now() - 30000
    );

    if (isAiTyping) {
      return (
        <span className="text-blue-500 dark:text-blue-400 italic text-[12.5px] animate-pulse font-medium flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-ping" />
          Bia está digitando...
        </span>
      );
    }

    const preview = lead.last_message_preview;
    if (!preview) {
      return <span className="text-[#8696a0]">Sem mensagens ainda</span>;
    }

    const cleaned = cleanPreviewText(preview);
    const lowerCleaned = safeLower(cleaned);

    const isAudio = lowerCleaned.includes('[áudio]') || lowerCleaned.includes('[audio]') || lowerCleaned.includes('audio') || lowerCleaned.includes('<audio');
    const isImage = lowerCleaned.includes('[imagem]') || lowerCleaned.includes('[sticker]') || lowerCleaned.includes('imagem') || lowerCleaned.includes('sticker');
    const isVideo = lowerCleaned.includes('[vídeo]') || lowerCleaned.includes('[video]') || lowerCleaned.includes('video');
    const isDoc = lowerCleaned.includes('[documento]') || lowerCleaned.includes('documento') || lowerCleaned.includes('pdf');

    if (isAudio) {
      return (
        <div className="flex items-center gap-1">
          <Mic size={15} className={cn(
            "shrink-0",
            hasUnread ? "text-emerald-700 dark:text-[#00e676]" : "text-[#8696a0] dark:text-[#8696a0]"
          )} />
          <span className={cn(
            "truncate",
            hasUnread ? "text-emerald-700 dark:text-[#00e676] font-medium" : "text-[#8696a0] dark:text-[#8696a0]"
          )}>
            Mensagem de voz
          </span>
        </div>
      );
    }

    if (isImage) {
      return (
        <div className="flex items-center gap-1 text-[#8696a0]">
          <Image size={15} className="shrink-0 text-gray-400 dark:text-[#8696a0]" />
          <span className="truncate">Foto</span>
        </div>
      );
    }

    if (isVideo) {
      return (
        <div className="flex items-center gap-1 text-[#8696a0]">
          <VideoIcon size={15} className="shrink-0 text-gray-400 dark:text-[#8696a0]" />
          <span className="truncate">Vídeo</span>
        </div>
      );
    }

    if (isDoc) {
      return (
        <div className="flex items-center gap-1 text-[#8696a0]">
          <FileText size={15} className="shrink-0 text-gray-400 dark:text-[#8696a0]" />
          <span className="truncate">Documento</span>
        </div>
      );
    }

    return (
      <span className={cn(
        "truncate",
        hasUnread ? "text-gray-900 dark:text-gray-100 font-medium" : "text-[#8696a0] dark:text-[#8696a0]"
      )}>
        {cleaned}
      </span>
    );
  };

  // Source of truth: conversations vindo do backend /api/conversations (com lead join) + leads órfãos (sem conversa)
  const conversationItems = useMemo(() => {
    // Indexação O(M) dos leads em memória para busca O(1) por ID (evita O(N x M) nested scan)
    const leadsMap = new Map<string, Lead>();
    for (let i = 0; i < leads.length; i++) {
      const l = leads[i];
      if (l && l.id) leadsMap.set(l.id, l);
    }

    const items = conversations.map(conv => {
      const rawLead = conv.lead || (conv.lead_id ? leadsMap.get(conv.lead_id) : undefined) || {};
      const leadObj = normalizeLeadForUI(rawLead);

      const mergedLead: Lead = {
        ...leadObj,
        id: leadObj.id || conv.lead_id || conv.id,
        nome: leadObj.nome || (rawLead as any)?.nome || 'Sem nome',
        telefone: leadObj.telefone || (rawLead as any)?.telefone || '',
        unread_count: conv.unread_count !== undefined ? conv.unread_count : (leadObj.unread_count || 0),
        last_message_at: conv.last_message_at || leadObj.last_message_at,
        last_message_preview: conv.last_message_preview || leadObj.last_message_preview,
        last_message_sender_type: (conv.last_message_sender_type as any) || leadObj.last_message_sender_type,
        is_archived: conv.is_archived !== undefined ? conv.is_archived : leadObj.is_archived,
        responsavel_user_id: conv.assigned_user_id || leadObj.responsavel_user_id,
        atendimento_finalizado: conv.status === 'resolved' || leadObj.atendimento_finalizado
      };

      return {
        conv,
        lead: mergedLead
      };
    });

    // Merge com leads órfãos (sem conversa criada ainda)
    const convLeadIds = new Set(conversations.map(c => c.lead_id).filter(Boolean));
    const orphanLeads = leads
      .filter(l => l?.id && !convLeadIds.has(l.id))
      .map(rawLead => {
        const leadObj = normalizeLeadForUI(rawLead);
        return {
          conv: undefined as any,
          lead: leadObj
        };
      });

    // Após montar orphanLeads, filtrar se estiver na aba "minhas":
    const filteredOrphans = orphanLeads.filter(item => {
      if (!showArchived && mainTab === 'minhas') {
        return item.lead.responsavel_user_id && myUserIds.has(item.lead.responsavel_user_id);
      }
      return true;
    });

    return [...items, ...filteredOrphans];
  }, [conversations, leads, mainTab, myUserIds, showArchived]);

  const filteredItems = useMemo(() => {
    return conversationItems.filter(({ conv, lead }) => {
      // Filtrar por loja ativa (garantia adicional no frontend)
      const itemLojaId = conv?.loja_id || lead.loja_id;
      if (lojaAtivaId && itemLojaId && itemLojaId !== lojaAtivaId) {
        return false;
      }

      // Arquivadas
      const isArchived = Boolean(conv ? conv.is_archived : lead.is_archived);
      if (showArchived ? !isArchived : isArchived) {
        return false;
      }

      // Filtragem por abas (Minhas / Todas / Transbordo)
      if (!showArchived && mainTab === 'minhas') {
        if (conv) {
          const isMine = isAssignedToMe(conv) || Boolean(conv.assigned_user_id && myUserIds.has(conv.assigned_user_id));
          const notSla = !conv.sla_exceeded;
          if (!isMine || !notSla) return false;
        } else {
          const isMine = Boolean(lead.responsavel_user_id && myUserIds.has(lead.responsavel_user_id));
          if (!isMine) return false;
        }
      } else if (!showArchived && mainTab === 'transbordo') {
        if (!conv || !conv.sla_exceeded) {
          return false;
        }
      }

      // Conversas ja foram buscadas no servidor (inclui preview e semantica ILIKE).
      // Refiltrar somente cache de leads sem conversa e a janela de debounce.
      if (search.trim() && (!conv || search.trim() !== debouncedSearch)) {
        const s = safeLower(search.trim());
        const nameMatch = safeLower(lead.nome).includes(s);
        const phoneMatch = safeText(lead.telefone).includes(search.trim());
        const previewMatch = safeLower(conv?.last_message_preview || lead.last_message_preview || '').includes(s);
        if (!nameMatch && !phoneMatch && !previewMatch) {
          return false;
        }
      }

      // Sub-filtros
      if (filter === 'nao_lidos') {
        return (lead.unread_count || 0) > 0 || (conv?.unread_count || 0) > 0;
      }
      if (filter === 'em_atendimento') {
        return ['Em atendimento', 'Em Atendimento', 'Em Qualificação', 'Novo Lead', 'Data Verificada'].includes(lead.status_atendimento || '') || conv?.status === 'open';
      }
      if (filter === 'agendados') {
        return safeLower(lead.status_atendimento || '').includes('agend');
      }
      if (filter === 'finalizados') {
        return lead.atendimento_finalizado === true || conv?.status === 'resolved';
      }

      return true;
    });
  }, [conversationItems, lojaAtivaId, showArchived, mainTab, myUserIds, search, debouncedSearch, filter]);

  const tagLojaIds = useMemo(() => Array.from(new Set(filteredItems.flatMap(({ conv, lead }) => {
    const lojaId = conv?.loja_id || lead.loja_id;
    return lojaId && lojaId !== 'all' && lead.tags?.length ? [lojaId] : [];
  }))), [filteredItems]);
  useEffect(() => {
    for (const lojaId of tagLojaIds) void fetchTagsForLoja(lojaId);
  }, [tagLojaIds, fetchTagsForLoja]);

  // A capacidade de renderização não deve encolher junto de uma página transitória.
  // O reset por filtro mantém 150; scroll continua aumentando a capacidade.

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#111B21] border-r border-gray-200 dark:border-gray-800 transition-colors duration-300">
      {/* Top Header styled like WhatsApp Web */}
      <div className="h-[60px] flex items-center justify-between px-4 bg-[#f0f2f5] dark:bg-[#202c33] border-b border-[#e9edef] dark:border-[#313d45]/20 select-none shrink-0 transition-colors duration-300">
        <div className="flex items-center gap-3">
          {showArchived ? (
            <button
              onClick={() => onToggleArchived?.(false)}
              className="p-1 text-gray-600 dark:text-[#aebac1] hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer animate-in fade-in slide-in-from-left-2 duration-200"
              title="Voltar para Ativas"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-mobile-sidebar'))}
              className="md:hidden p-1 text-gray-600 dark:text-[#aebac1] hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer"
              title="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          <h2 className="text-base md:text-[19px] font-bold text-gray-900 dark:text-[#e9edef] tracking-tight">
            {showArchived ? 'Arquivadas' : 'Conversas'}
          </h2>

          <span className="text-[11px] bg-gray-200/60 dark:bg-[#374248] text-gray-700 dark:text-[#aebac1] px-2 py-0.5 rounded-full font-bold">
            {filteredItems.length}
          </span>
        </div>


      </div>

      {/* Account / Tenant selector if SuperAdmin */}
      {isSuperAdmin && (
        <div className="p-2 bg-gray-50 dark:bg-[#111B21] border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="relative flex items-center">
            <Building2 className="w-3.5 h-3.5 absolute left-2.5 text-blue-500 pointer-events-none" />
            <select aria-label="Filtrar conversas por empresa"
              value={selectedTenantId}
              onChange={(e) => handleTenantChange(e.target.value)}
              className="w-full pl-8 pr-2 py-1 bg-blue-50/50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-lg text-xs font-semibold text-blue-900 dark:text-blue-200 focus:outline-none cursor-pointer"
            >
              <option value="">🏢 Todos os Tenants (Super Admin)</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} ({t.status})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Search and Filters Section */}
      <div className="bg-white dark:bg-[#111B21] border-b border-gray-100 dark:border-gray-800/20 flex flex-col shrink-0">
        {/* Search */}
        <div className="p-3 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-[#8696a0]" />
            <input
              type="text"
              placeholder="Pesquisar ou começar uma nova conversa"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-1.5 bg-[#f0f2f5] dark:bg-[#202C33] border-none rounded-lg text-[13.5px] transition-colors text-gray-900 dark:text-[#e9edef] placeholder-gray-500 dark:placeholder-[#8696a0] focus:outline-none focus:ring-0"
            />
          </div>
        </div>

        {/* Main Tabs (Minhas / Todas / Transbordo) */}
        {!showArchived && (
          <div className="flex border-b border-gray-100 dark:border-gray-800 shrink-0 px-1">
            <button
              onClick={() => setMainTab('minhas')}
              className={cn(
                "flex-1 py-2 text-[13px] font-bold text-center border-b-2 transition-all cursor-pointer relative",
                mainTab === 'minhas'
                  ? "border-emerald-500 text-emerald-700 dark:text-[#00e676]"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-[#8696a0] dark:hover:text-[#e9edef]"
              )}
            >
              Minhas
            </button>
            <button
              onClick={() => setMainTab('todas')}
              className={cn(
                "flex-1 py-2 text-[13px] font-bold text-center border-b-2 transition-all cursor-pointer relative",
                mainTab === 'todas'
                  ? "border-emerald-500 text-emerald-700 dark:text-[#00e676]"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-[#8696a0] dark:hover:text-[#e9edef]"
              )}
            >
              Todas
            </button>
            <button
              onClick={() => setMainTab('transbordo')}
              className={cn(
                "flex-1 py-2 text-[13px] font-bold text-center border-b-2 transition-all cursor-pointer relative flex items-center justify-center gap-1",
                mainTab === 'transbordo'
                  ? "border-rose-500 text-rose-600 dark:text-rose-450"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-[#8696a0] dark:hover:text-[#e9edef]"
              )}
            >
              <span>Transbordo</span>
              {transbordoCount > 0 && (
                <span className="bg-rose-500 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full min-w-[16px] text-center shrink-0">
                  {transbordoCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Sub-Filters */}
        <div className="p-3 pt-2 flex flex-nowrap gap-1.5 overflow-x-auto pb-2.5 no-scrollbar scroll-smooth">
          {filterOptions.map(opt => (
            <button
              key={opt.id}
              onClick={() => setFilter(opt.id)}
              className={cn(
                "px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap transition-all cursor-pointer",
                filter === opt.id
                  ? "bg-[#e8f9f5] text-emerald-700 dark:bg-[#0a332c] dark:text-[#00e676]"
                  : "bg-[#f0f2f5] dark:bg-[#202C33] text-[#54656f] dark:text-[#aebac1] hover:bg-gray-200 dark:hover:bg-[#2a3942]"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Banner de Notificações - Exibido apenas se o navegador não tiver permissão concedida */}
      {notificationBannerKey && notificationPermission === 'default' && dismissedBannerScope !== notificationBannerKey && (
        <div className="flex items-start gap-3.5 p-3.5 bg-[#53bdeb]/15 dark:bg-[#182229] border-b border-gray-100/50 dark:border-gray-800/40 transition-all select-none shrink-0">
          <div className="w-9 h-9 rounded-full bg-[#53bdeb] dark:bg-[#182229] border border-[#53bdeb]/20 flex items-center justify-center text-[#111b21] dark:text-[#53bdeb] shrink-0">
            <BellOff size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-gray-900 dark:text-[#e9edef] leading-tight">
              Receba avisos de mensagens na área de trabalho.
            </p>
            <button
              onClick={handleEnableDesktopNotifications}
              className="text-[11.5px] font-bold text-emerald-700 dark:text-[#00e676] hover:underline mt-0.5 text-left block cursor-pointer"
            >
              Ativar notificações de área de trabalho
            </button>
          </div>
          <button
            onClick={dismissNotificationBanner}
            aria-label="Fechar aviso de notificações"
            className="text-gray-400 hover:text-gray-600 dark:text-[#8696a0] dark:hover:text-[#e9edef] p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
            title="Fechar aviso"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Archived Chats Quick Entry */}
      {!showArchived && (
        <div
          onClick={() => onToggleArchived?.(true)}
          className="flex items-center gap-4 px-4 py-3 hover:bg-[#f5f6f6] dark:hover:bg-[#202c33] cursor-pointer transition-colors border-b border-gray-100/50 dark:border-[#202c33]/40 select-none shrink-0"
        >
          <div className="text-emerald-700 dark:text-[#00a884] shrink-0">
            <Archive size={18} />
          </div>
          <span className="flex-1 text-[14px] font-medium text-gray-800 dark:text-[#e9edef]">
            Arquivadas
          </span>
          <span className="text-[12px] text-emerald-700 dark:text-[#00e676] font-bold">
            {archivedCount?.scope === archiveScope ? archivedCount.count : '—'}
          </span>
        </div>
      )}

      {/* Sync Error Banner */}
      {syncError && filteredItems.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/50 px-3.5 py-2 flex items-center justify-between text-xs text-amber-800 dark:text-amber-200 select-none shrink-0 animate-in fade-in duration-200">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="truncate font-medium">Erro ao sincronizar conversas recentes</span>
          </div>
          <button
            onClick={() => refetch()}
            className="text-amber-700 dark:text-amber-300 font-bold underline hover:no-underline cursor-pointer shrink-0 ml-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Conversations List ou Erro Inicial */}
      {isError && filteredItems.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center select-none bg-white dark:bg-[#111B21]">
          <div className="w-12 h-12 rounded-full bg-rose-100 dark:bg-rose-950/40 text-rose-500 flex items-center justify-center mb-3">
            <AlertCircle className="w-6 h-6" />
          </div>
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            Não foi possível carregar as conversas
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4 max-w-xs">
            Ocorreu uma falha ao buscar as conversas. Verifique sua conexão e tente novamente.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-[#00a884] hover:bg-[#008f6f] text-white text-xs font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden no-scrollbar bg-white dark:bg-[#111B21]"
          onScroll={(e) => {
            const target = e.currentTarget;
            if (target.scrollHeight - target.scrollTop - target.clientHeight < 150) {
              if (displayCount < filteredItems.length) {
                setDisplayCount(prev => Math.min(prev + 100, filteredItems.length));
              } else if (hasNextPage && !isFetchingNextPage) {
                loadMore();
              }
            }
          }}
        >
          {filteredItems.slice(0, displayCount).map(({ conv, lead }, index) => {
            const isSelected = selectedLeadId === lead.id || (conv && selectedLeadId === conv.id);
            const isChecked = selectedIds.includes(lead.id);
            const hasUnread = (lead.unread_count || 0) > 0 || (conv?.unread_count || 0) > 0;
            const waitingText = getWaitingHoursText(lead.last_message_at, lead.last_message_sender_type);
            const formattedTags = lead.tags
              ? Array.from(new Set(lead.tags.filter(Boolean).map(t => String(t).trim())))
              : [];
            const lastMsgText = getCleanedFriendlyPreview(lead.last_message_preview) || 'Sem mensagens ainda';
            const isSentByMe = lead.last_message_sender_type === 'humano' || lead.last_message_sender_type === 'ia';

            return (
              <ChatListItem
                key={conv?.id || lead.id || `lead-${lead.telefone || lead.remotejid || index}`}
                name={lead.nome || lead.telefone || 'Sem nome'}
                avatar={lead.avatar_url}
                lastMessage={lastMsgText}
                lastMessageStatus={isSentByMe ? 'read' : undefined}
                timestamp={lead.last_message_at ? formatSafe(lead.last_message_at, 'HH:mm') : ''}
                unreadCount={lead.unread_count || conv?.unread_count || 0}
                isSelected={isSelected}
                onClick={() => onSelectLead(lead.id, lead, conv || undefined)}
                className="border-b border-wa-border/40 dark:border-wa-border/20"
              >
                {/* Line 3: Tags chips + Waiting badge + Archive action */}
                <div className="flex items-center justify-between gap-1.5 w-full pt-0.5">
                  <div className="flex items-center gap-1 flex-wrap min-w-0">
                    {formattedTags.slice(0, 2).map(tag => {
                      const resolved = resolveTag(conv?.loja_id || lead.loja_id, tag);
                      return (
                        <span
                          key={tag}
                          className="text-[9px] font-bold px-1.5 py-0.2 rounded text-white shadow-2xs truncate max-w-[180px]"
                          style={badgeColors(resolved.color)}
                          title={resolved.label}
                        >
                          {resolved.label}
                        </span>
                      );
                    })}
                    {formattedTags.length > 2 && (
                      <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500">
                        +{formattedTags.length - 2}
                      </span>
                    )}

                    {waitingText && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700/50">
                        <Clock size={10} />
                        {waitingText}
                      </span>
                    )}

                    {(conv?.assigned_user_id || lead.responsavel_user_id) && (
                      <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-300 dark:border-amber-700/50 shrink-0">
                        {isAssignedToMe(conv) || (lead.responsavel_user_id && myUserIds.has(lead.responsavel_user_id))
                          ? 'Você'
                          : (conv?.assigned_user_name ||
                             (lead as any)?.responsavel_nome ||
                             users.find(u => u.id === (conv?.assigned_user_id || lead.responsavel_user_id) || (u as any).auth_user_id === (conv?.assigned_user_id || lead.responsavel_user_id))?.nome ||
                             'Atribuído')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {lead.responsavel_user_id && (
                      <div
                        className="w-4 h-4 rounded-full bg-blue-600 text-white text-[8px] font-bold flex items-center justify-center shrink-0 shadow-2xs"
                        title={myUserIds.has(lead.responsavel_user_id) ? "Você é o responsável" : "Lead com responsável"}
                      >
                        <User size={10} />
                      </div>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const nextArchived = !showArchived;
                        archiveConversation(conv?.id || lead.id, nextArchived);
                      }}
                      className="p-1 text-gray-400 hover:text-emerald-700 dark:hover:text-[#00e676] hover:bg-gray-200/50 dark:hover:bg-white/5 rounded-full transition-all shrink-0 cursor-pointer"
                      title={showArchived ? "Desarquivar conversa" : "Arquivar conversa"}
                    >
                      {showArchived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
                    </button>
                  </div>
                </div>
              </ChatListItem>
            );
          })}

          {isFetchingNextPage && (
            <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400 font-medium animate-pulse">
              Carregando mais conversas...
            </div>
          )}

          {filteredItems.length > displayCount && !isFetchingNextPage && (
            <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400 font-medium">
              Carregando mais conversas ({displayCount} de {filteredItems.length})...
            </div>
          )}

          {filteredItems.length === 0 && !isLoading && (
            <div className="p-8 text-center text-gray-400 text-xs">
              Nenhuma conversa encontrada.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

